import { CalendarClock, CircleCheck, Clock, RefreshCw, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api } from '@/api';
import type { ComplianceDeadline, DeadlineStatus } from '@/api/areas/governance';
import { Callout, DataTable, Section, StatCard, StatGrid, FilterBar, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { Button } from '@/components/ui/button';
import { formatCount, formatDate, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { DueCell, EnumBadge, SelectField, useEnumOptions } from '../compliance/ui';
import { REPORT_STATUS } from '../compliance/vnnic/shared';

const DEADLINE_STATUS = { open: { tone: 'pending' }, done: { tone: 'ok' }, waived: { tone: 'neutral' } } as const;

/** `VNNIC_0805B` -> `VNNIC 0805B`: the form codes are names, not words to title-case. */
const obligationName = (obligation: string): string => obligation.replace(/_/g, ' ');

/** `/compliance/deadlines`: what the registrar owes a regulator and when, overdue first. */
export function DeadlinesPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const options = useEnumOptions();
  useDocumentTitle(t('deadlines.title'));
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as DeadlineStatus | '';
  const obligation = params.get('obligation') ?? '';
  const result = useApi(
    (signal) => api.listComplianceDeadlines({ status: status || undefined, obligation: obligation || undefined, limit: 200 }, { signal }),
    [status, obligation],
  );
  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  const filtered = Boolean(status || obligation);
  const items = result.status === 'success' ? result.data.items : [];
  const overdue = result.status === 'success' ? result.data.overdue_count : 0;
  const done = items.filter((d) => d.status === 'done').length;
  const next = items.find((d) => d.status === 'open' && !d.overdue);

  const columns: Column<ComplianceDeadline>[] = [
    {
      key: 'obligation',
      header: t('deadlines.col.obligation'),
      cell: (d) => (
        <div className="text-sm">
          <span className="font-medium">{obligationName(d.obligation)}</span>
          <div className="text-xs text-muted-foreground">{d.period_start ? d.reference : `${d.reference} · ${t('deadlines.noPeriod')}`}</div>
        </div>
      ),
    },
    {
      key: 'due',
      header: t('deadlines.col.due'),
      cell: (d) => <DueCell dueAt={d.due_at} overdue={d.overdue} done={d.status === 'open' ? undefined : formatDate(d.due_at)} />,
    },
    {
      key: 'status',
      header: t('deadlines.col.status'),
      cell: (d) => <EnumBadge group="deadlineStatus" value={d.status} tones={DEADLINE_STATUS} />,
    },
    {
      key: 'report',
      header: t('deadlines.col.report'),
      cell: (d) =>
        d.report ? (
          <div className="space-y-1 text-sm">
            <Link to={`/compliance/vnnic/international-reports/${encodeURIComponent(d.report.id)}`} className="font-medium text-primary hover:underline">
              {d.report.report_type} · {t('deadlines.revision', { revision: d.report.revision })}
            </Link>
            <EnumBadge group="reportStatus" value={d.report.status} tones={REPORT_STATUS} />
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">{t('deadlines.noReport')}</span>
        ),
    },
    {
      key: 'owner',
      header: t('deadlines.col.owner'),
      cell: (d) => (
        <div className="max-w-56 text-sm">
          {d.owner_team}
          <div className="truncate text-xs text-muted-foreground" title={d.source_reference}>
            {t('deadlines.col.source')}: {d.source_reference}
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('deadlines.title')}
        crumbs={[{ label: t('crumbs.compliance') }]}
        subtitle={t('deadlines.subtitle')}
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-6">
        {overdue > 0 ? (
          <Callout tone="crit" title={t('sla.overdue')}>
            {t('deadlines.overdueNote')}
          </Callout>
        ) : null}
        <Callout tone="info">{t('deadlines.sourceNote')}</Callout>
        {result.status === 'success' ? (
          <StatGrid>
            <StatCard label={t('deadlines.stats.open')} value={formatCount(result.data.open_count)} icon={CalendarClock} />
            <StatCard
              label={t('deadlines.stats.overdue')}
              value={formatCount(overdue)}
              tone={overdue > 0 ? 'crit' : 'ok'}
              status={overdue > 0 ? t('sla.overdue') : undefined}
              icon={Clock}
            />
            <StatCard label={t('deadlines.stats.done')} value={formatCount(done)} tone={done > 0 ? 'ok' : 'neutral'} icon={CircleCheck} />
            <StatCard
              label={t('deadlines.stats.next')}
              value={next ? formatRelative(next.due_at) : '—'}
              hint={next ? `${obligationName(next.obligation)} · ${next.reference}` : t('deadlines.none')}
            />
          </StatGrid>
        ) : null}
        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              filtered ? (
                <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams())}>
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('deadlines.filter.status')}
              value={status}
              allLabel={t('common.any')}
              options={options('deadlineStatus', result.status === 'success' ? result.data.statuses : [])}
              onChange={(v) => set('status', v)}
              className="w-full sm:w-48"
            />
            <SelectField
              label={t('deadlines.filter.obligation')}
              value={obligation}
              allLabel={t('common.any')}
              options={(result.status === 'success' ? result.data.obligations : []).map((o) => ({ value: o, label: obligationName(o) }))}
              onChange={(v) => set('obligation', v)}
              className="w-full sm:w-72"
            />
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={5} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('deadlines.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? (
            <DataTable
              columns={columns}
              rows={items}
              rowKey={(d) => d.id}
              empty={<EmptyState title={t('deadlines.empty')}>{filtered ? t('deadlines.emptyHint') : null}</EmptyState>}
            />
          ) : null}
        </Section>
      </div>
    </>
  );
}
